import { useMainRouteParams } from "@/hooks/pane-routing";
import { emitCanvasOpenChat } from "@/pages/canvas/hooks/useCanvasWindowEvents";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  fileRecordTextSelectionQueryKey,
  normalizeFileRecordTextSelectionCache,
  type FileRecordTextSelectionCache,
} from "./fileRecordTextSelection";

export function usePushFileRecordTextSelectionToChat(params: {
  fileRecordId: string;
  fileRecordName: string;
}) {
  const { fileRecordId, fileRecordName } = params;
  const { canvasId, flowSessionId } = useMainRouteParams();
  // The file-record pane overlays exactly one chat surface at a time: a canvas
  // (whose floating chat session can change, so selections are keyed by the
  // stable canvasId) or the workspace chat route (keyed by its flowSessionId).
  // Only one of these route params is ever present, so prefer canvasId.
  const scopeId = canvasId ?? flowSessionId;
  const queryClient = useQueryClient();
  const enabled = !!scopeId;

  const pushSelection = useCallback(
    (selectedText: string) => {
      if (!scopeId || !fileRecordId) return;
      const trimmed = selectedText.trim();
      if (!trimmed) return;

      // Canvas's agent chat may be collapsed — nudge it open. The workspace
      // chat is always mounted, so there is nothing to open there.
      if (canvasId) emitCanvasOpenChat({ canvasId });
      queryClient.setQueryData<FileRecordTextSelectionCache[]>(
        fileRecordTextSelectionQueryKey(scopeId),
        (prev) => {
          const existing = normalizeFileRecordTextSelectionCache(prev);
          const duplicate = existing.some(
            (entry) =>
              entry.fileRecordId === fileRecordId &&
              entry.selectedText === trimmed,
          );
          if (duplicate) return existing;

          return [
            ...existing,
            {
              fileRecordId,
              fileRecordName,
              selectedText: trimmed,
              selectionId: uuidv4(),
            },
          ];
        },
      );
    },
    [scopeId, canvasId, fileRecordId, fileRecordName, queryClient],
  );

  return { enabled, pushSelection };
}
